import { Show, useRecordContext } from 'react-admin';
import {
  Avatar,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { EmptyHint, FieldRow, Section } from '../../components/Section';
import { StatusChip } from '../../components/StatusChip';
import { InvitationLink, ReportLink, UserLink } from '../../components/EntityLink';
import { SuspendUserButton } from './UserActions';
import {
  auditActionLabel,
  formatDate,
  formatDateTime,
  genderLabels,
  invitationCategoryLabels,
  invitationStatusLabels,
  label,
  ratingEventTypeLabels,
  reportCategoryLabels,
  reportStatusLabels,
  userStatusLabels,
} from '../../i18n/labels';
import type { UserDetail } from '../../types';

function UserTitle(): JSX.Element {
  const record = useRecordContext<UserDetail>();
  return <span>{record ? `Пользователь: ${record.displayName ?? record.id}` : ''}</span>;
}

function UserShowLayout(): JSX.Element | null {
  const record = useRecordContext<UserDetail>();
  if (!record) return null;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Section title="Профиль" action={<SuspendUserButton />}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          <Avatar
            src={record.avatarUrl ?? undefined}
            alt={record.displayName ?? ''}
            sx={{ width: 96, height: 96 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              {record.displayName ?? 'Без имени'}
              <StatusChip status={record.status} dict={userStatusLabels} />
            </Typography>
            <FieldRow label="Телефон" value={record.phone} />
            <FieldRow label="Email" value={record.email} />
            <FieldRow label="Пол" value={label(genderLabels, record.gender)} />
            <FieldRow label="Дата рождения" value={formatDate(record.birthDate)} />
            <FieldRow label="Регион" value={record.regionName} />
            <FieldRow label="Рейтинг" value={record.rating} />
            <FieldRow label="О себе" value={record.bio} />
            <FieldRow label="Инвайт-код" value={record.inviteCode} />
            <FieldRow
              label="Онбординг"
              value={record.onboardingDone ? 'Завершён' : 'Не завершён'}
            />
            <FieldRow label="Зарегистрирован" value={formatDateTime(record.createdAt)} />
            {record.deletedAt ? (
              <FieldRow label="Удалён" value={formatDateTime(record.deletedAt)} />
            ) : null}
          </Box>
        </Box>
      </Section>

      <Section title="Инвайт-связи (фундамент MLM)">
        <FieldRow
          label="Кто пригласил"
          value={
            record.invitedBy ? (
              <UserLink id={record.invitedBy.id} label={record.invitedBy.displayName} />
            ) : (
              'Органическая регистрация'
            )
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
          Пригласил пользователей: {record.invitedUsers.length}
        </Typography>
        {record.invitedUsers.length > 0 ? (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {record.invitedUsers.map((u) => (
              <UserLink key={u.id} id={u.id} label={u.displayName} />
            ))}
          </Stack>
        ) : (
          <EmptyHint text="Пока никого не пригласил" />
        )}
      </Section>

      <Section title={`Приглашения (последние ${record.invitations.length})`}>
        {record.invitations.length === 0 ? (
          <EmptyHint text="Приглашений нет" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Категория</TableCell>
                <TableCell>Место</TableCell>
                <TableCell>Когда</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Лайки</TableCell>
                <TableCell align="right">Матчи</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {record.invitations.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>{label(invitationCategoryLabels, i.category)}</TableCell>
                  <TableCell>{i.placeName}</TableCell>
                  <TableCell>{formatDateTime(i.scheduledAt)}</TableCell>
                  <TableCell>
                    <StatusChip status={i.status} dict={invitationStatusLabels} />
                  </TableCell>
                  <TableCell align="right">{i.likesCount}</TableCell>
                  <TableCell align="right">{i.matchesCount}</TableCell>
                  <TableCell>
                    <InvitationLink id={i.id} label="Открыть" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title={`Жалобы на пользователя (последние ${record.reportsReceived.length})`}>
        {record.reportsReceived.length === 0 ? (
          <EmptyHint text="Жалоб нет" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Категория</TableCell>
                <TableCell>От кого</TableCell>
                <TableCell>Комментарий</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Создана</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {record.reportsReceived.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{label(reportCategoryLabels, r.category)}</TableCell>
                  <TableCell>
                    <UserLink id={r.reporter.id} label={r.reporter.displayName} />
                  </TableCell>
                  <TableCell>{r.comment ?? '—'}</TableCell>
                  <TableCell>
                    <StatusChip status={r.status} dict={reportStatusLabels} />
                  </TableCell>
                  <TableCell>{formatDateTime(r.createdAt)}</TableCell>
                  <TableCell>
                    <ReportLink id={r.id} label="Открыть" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title={`История рейтинга (последние ${record.ratingEvents.length})`}>
        {record.ratingEvents.length === 0 ? (
          <EmptyHint text="Событий рейтинга нет" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Событие</TableCell>
                <TableCell align="right">Дельта</TableCell>
                <TableCell align="right">Итог</TableCell>
                <TableCell>Причина</TableCell>
                <TableCell>Когда</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.ratingEvents.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{label(ratingEventTypeLabels, e.type)}</TableCell>
                  <TableCell align="right">{e.delta > 0 ? `+${e.delta}` : e.delta}</TableCell>
                  <TableCell align="right">{e.resultValue}</TableCell>
                  <TableCell>{e.reason ?? '—'}</TableCell>
                  <TableCell>{formatDateTime(e.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title={`Журнал действий (последние ${record.auditTail.length})`}>
        {record.auditTail.length === 0 ? (
          <EmptyHint text="Записей нет" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Когда</TableCell>
                <TableCell>Действие</TableCell>
                <TableCell>Объект</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.auditTail.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{formatDateTime(a.createdAt)}</TableCell>
                  <TableCell>{auditActionLabel(a.action)}</TableCell>
                  <TableCell>
                    {a.resourceType ? `${a.resourceType} ${a.resourceId ?? ''}` : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

export function UserShow(): JSX.Element {
  return (
    <Show title={<UserTitle />} component="div">
      <UserShowLayout />
    </Show>
  );
}
